// CSR-855: card layout for the Insurance Companies list. Matches the
// lodestar /agencies?tab=insurers prototype:
//   * Avatar tile (Building2 icon) + name + NAIC stacked on the left
//   * Category pill (color-coded per category) + edit pencil on the right
//   * Vertical info list with small leading icons (Parent, child count,
//     City+State, Globe+website, FileText+policy count)
//   * Border-top row showing New Commission / Renewal Commission
//     (hidden for parent_company per PRD)
//
// CSR-822: introduced this card; CSR-823 wired the edit pencil into the
// shared Add/Edit dialog mounted in Companies.tsx.

import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, FileText, Globe, Pencil } from "lucide-react";
import type { InsuranceCompany } from "@shared/schema";
import { useInsuranceCompanyPolicyCount } from "@/lib/api";
import { cn } from "@/lib/utils";

function formatCommission(value: string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  // Server stores percentages as decimals with 2 places of precision
  // (e.g. "12.50"). Display without trailing zeros for whole percents.
  return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}%`;
}

const CATEGORY_LABEL: Record<InsuranceCompany["category"], string> = {
  parent_company: "Parent Company",
  insurance_carrier: "Insurance Carrier",
  mga: "MGA",
};

// Per-category pill colors mirror the lodestar prototype.
const CATEGORY_BADGE: Record<InsuranceCompany["category"], string> = {
  parent_company: "bg-purple-500/10 text-purple-700 border-purple-200",
  insurance_carrier: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  mga: "bg-blue-500/10 text-blue-700 border-blue-200",
};

function CategoryBadge({ category }: { category: InsuranceCompany["category"] }) {
  return (
    <span
      className={cn(
        "px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap",
        CATEGORY_BADGE[category],
      )}
      data-testid="badge-category"
    >
      {CATEGORY_LABEL[category]}
    </span>
  );
}

interface InsuranceCompanyCardProps {
  company: InsuranceCompany;
  childCount?: number;
  parentName?: string;
  onEdit: (company: InsuranceCompany) => void;
}

export function InsuranceCompanyCard({
  company,
  childCount,
  parentName,
  onEdit,
}: InsuranceCompanyCardProps) {
  const [, navigate] = useLocation();
  const { data: policyCount, isLoading: policyCountLoading } =
    useInsuranceCompanyPolicyCount(company.id);

  const handleCardClick = () => {
    navigate(`/companies/insurers/${company.id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(company);
  };

  const policyCountLabel = policyCountLoading
    ? "…"
    : typeof policyCount === "number"
    ? String(policyCount)
    : "-";

  const isParent = company.category === "parent_company";
  const childCountValue = childCount ?? 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className="p-5 cursor-pointer hover-elevate transition-shadow"
      data-testid={`card-insurance-company-${company.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3
              className="font-semibold text-foreground truncate"
              title={company.name}
            >
              {company.name}
            </h3>
            {company.naic && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="stat-naic"
              >
                NAIC: {company.naic}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <CategoryBadge category={company.category} />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleEditClick}
            aria-label={`Edit ${company.name}`}
            data-testid={`button-edit-${company.id}`}
          >
            <Pencil className="w-4 h-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        {parentName && (
          <p className="text-xs text-muted-foreground" data-testid="stat-parent">
            Parent:{" "}
            <span className="font-medium text-foreground">{parentName}</span>
          </p>
        )}

        {isParent && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="stat-child-count"
          >
            {childCountValue}{" "}
            {childCountValue === 1 ? "child company" : "child companies"}
          </p>
        )}

        {company.website && (
          <div
            className="flex items-center gap-1.5 text-muted-foreground"
            data-testid="stat-website"
          >
            <Globe className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{company.website}</span>
          </div>
        )}

        <div
          className="flex items-center gap-1.5 text-muted-foreground"
          data-testid="stat-policy-count"
        >
          <FileText className="w-3.5 h-3.5 shrink-0" />
          <span>{policyCountLabel} policies</span>
        </div>

        {!isParent && (
          <div className="flex gap-4 pt-2 border-t" data-testid="stat-commission">
            <div>
              <p className="text-xs text-muted-foreground">New Commission</p>
              <p className="font-medium">
                {formatCommission(company.newCommissionPct)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Renewal Commission</p>
              <p className="font-medium">
                {formatCommission(company.renewalCommissionPct)}
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
